const desktopMediaQuery = window.matchMedia('(min-width: 750px)');
const minimumScrollDelta = 1;

const parsePixels = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const initializeSection = (section) => {
  const details = section.querySelector('.product-details.sticky-content--desktop');

  if (!details || details.dataset.stickyBalanceInitialized === 'true') return;

  const detailsContent = details.querySelector('.group-block') ?? details;
  let lastScrollY = window.scrollY;
  let currentTop = null;
  let stickyState = {
    detailsHeight: 0,
    topLimit: 0,
    bottomLimit: 0,
    hasTravel: false,
  };

  const applyTop = () => {
    if (currentTop !== null) details.style.setProperty('--pdp-sticky-top', `${currentTop}px`);
  };

  const updateStickyState = () => {
    const detailsHeight = Math.ceil(detailsContent.getBoundingClientRect().height);

    if (detailsHeight > 0) details.style.setProperty('--pdp-details-height', `${detailsHeight}px`);

    const styles = getComputedStyle(details);
    const topLimit = parsePixels(styles.getPropertyValue('--sticky-header-offset'), 0);
    const bottomGap = parsePixels(styles.getPropertyValue('--pdp-sticky-bottom-gap'), 80);
    const bottomLimit = window.innerHeight - detailsHeight - bottomGap;
    const hasTravel = detailsHeight + topLimit + bottomGap > window.innerHeight;

    stickyState = {detailsHeight, topLimit, bottomLimit, hasTravel};
  };

  const clampTop = (value) => Math.min(stickyState.topLimit, Math.max(stickyState.bottomLimit, value));

  const synchronizeLayout = (resetTop = false) => {
    if (!desktopMediaQuery.matches) {
      details.style.removeProperty('--pdp-details-height');
      details.style.removeProperty('--pdp-sticky-top');
      currentTop = null;
      lastScrollY = window.scrollY;
      return;
    }

    updateStickyState();

    if (stickyState.detailsHeight <= 0) return;

    if (!stickyState.hasTravel) {
      currentTop = stickyState.topLimit;
      applyTop();
      lastScrollY = window.scrollY;
      return;
    }

    if (resetTop || currentTop === null) currentTop = stickyState.topLimit;

    currentTop = clampTop(currentTop);
    applyTop();
    lastScrollY = window.scrollY;
  };

  const requestLayoutSynchronization = () => window.requestAnimationFrame(() => synchronizeLayout());

  const synchronizeDuringTransition = (duration = 500) => {
    const start = performance.now();

    const tick = () => {
      synchronizeLayout();

      if (performance.now() - start < duration) window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);
  };

  const handleScroll = () => {
    if (!desktopMediaQuery.matches) return;

    const currentScrollY = window.scrollY;
    const delta = currentScrollY - lastScrollY;

    if (Math.abs(delta) < minimumScrollDelta) {
      lastScrollY = currentScrollY;
      return;
    }

    const styles = getComputedStyle(details);
    const bottomGap = parsePixels(styles.getPropertyValue('--pdp-sticky-bottom-gap'), 80);

    stickyState.topLimit = parsePixels(styles.getPropertyValue('--sticky-header-offset'), stickyState.topLimit);
    stickyState.bottomLimit = window.innerHeight - stickyState.detailsHeight - bottomGap;
    stickyState.hasTravel =
      stickyState.detailsHeight + stickyState.topLimit + bottomGap > window.innerHeight;

    if (!stickyState.hasTravel) {
      currentTop = stickyState.topLimit;
      applyTop();
      lastScrollY = currentScrollY;
      return;
    }

    if (currentTop === null) currentTop = stickyState.topLimit;

    currentTop = clampTop(currentTop - delta);
    applyTop();
    lastScrollY = currentScrollY;
  };

  new ResizeObserver(requestLayoutSynchronization).observe(detailsContent);

  section.addEventListener(
    'toggle',
    (event) => {
      if (event.target instanceof HTMLDetailsElement) synchronizeDuringTransition();
    },
    true
  );

  section.addEventListener(
    'transitionend',
    (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;

      if (target && (target.classList.contains('details-content') || target.closest('accordion-custom'))) {
        synchronizeDuringTransition(250);
      }
    },
    true
  );

  const handleViewportChange = () => synchronizeLayout(true);

  window.addEventListener('resize', handleViewportChange);
  desktopMediaQuery.addEventListener('change', handleViewportChange);
  window.addEventListener('scroll', handleScroll, {passive: true});
  window.setTimeout(() => synchronizeLayout(true), 300);
  synchronizeLayout(true);
  details.dataset.stickyBalanceInitialized = 'true';
};

const initializeStickyBalance = (root = document) => {
  root.querySelectorAll('.product-information').forEach((section) => initializeSection(section));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeStickyBalance());
} else {
  initializeStickyBalance();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (target) initializeStickyBalance(target);
});
